import { AddNewClientButton } from 'admin/features/clients/viewClients/AddNewClientButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetClientProfile } from 'admin/features/clients/viewClientDetails/accountInfoClientTab';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import { useGetSpecializationsQuery } from 'app/services/accountant';
import { BusinessProjectGetId } from 'app/services/responseTypes';
import { AddIcon, TrashCanSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import DateInputBasic from 'Components/Form/Basic/DateInputBasic';
import { MaskedInputBasic } from 'Components/Form/Basic/MaskedInputBasic';
import { PhoneNumInputBasic } from 'Components/Form/Basic/PhoneInputBasic';
import { SelectInputBasic } from 'Components/Form/Basic/SelectInputBasic';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import RadioInput from 'Components/Form/Inputs/RadioInput';
import Label from 'Components/Form/Label';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import UserBox from 'Components/UserBox';
import { countries } from 'countries';
import { ReactNode } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { states } from 'states';
import {
  BookkeepingSoftwareOptions,
  BusinessTypesOptions,
  FiscalYearStartOptions,
  OwenrshipOptions,
  validationSchema,
} from '../addBusinessDetails/formFields';
import { BusinessLogo } from '../previewBusinessDetails';

let OwnerObject = {
  firstname: '',
  lastname: '',
  ssn: '',
  ownership_percentage: '',
  address: '',
  country: '',
  city: '',
  state: '',
  zipcode: '',
  user_id: undefined,
};

export function BusinessForm({
  value: data,
  onSubmit,
  id,
  disabled,
  title,
  isLoading,
  button,
}: {
  value?: BusinessProjectGetId;
  title?: ReactNode;
  isLoading?: boolean;
  button?: ReactNode;
  disabled?: boolean;
  id?: string;
  onSubmit?: (v: BusinessProjectGetId) => void;
}) {
  const { client, isClient } = useGetClientProfile();
  const { businessId } = useParams();
  const isEditForm = !!businessId;

  const { data: specializations } = useGetSpecializationsQuery();
  const value = {
    ...data,
    specialization_id: data?.specialization?.id,
    business_owners: isEditForm
      ? data?.business_owners.map((bo) => ({
          ...bo,
          user_id: bo.user?.id,
        }))
      : [
          {
            user_id: client?.id,
            ownership_percentage: 100,
            user: client,
            address: client?.address,
            firstname: client?.given_name,
            lastname: client?.family_name,
            ssn: client?.ssn,
            country: client?.country,
            city: client?.city,
            state: client?.state,
            zipcode: client?.zipcode,
          },
        ],
  };

  let {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: value,
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'business_owners',
    keyName: 'array_field_uuid',
  });

  const watchFieldArray = watch('business_owners');

  const businessOwners = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  }) as BusinessProjectGetId['business_owners'];

  return (
    <>
      {title && (
        <DashboardContainerHeader
          title={
            <div className="flex gap-4 items-center">
              {data?.id && (
                <BusinessLogo businessId={data?.id} logo_url={data?.logo_url} />
              )}
              {data?.name}
            </div>
          }
        >
          <Button
            isLoading={isLoading}
            className="max-w-min"
            form="BusinessForm"
            type="submit"
          >
            {button}
          </Button>
        </DashboardContainerHeader>
      )}
      <ScrollContainer>
        <form
          id="BusinessForm"
          onSubmit={onSubmit && handleSubmit(onSubmit)}
          className="mx-6 w-2/3 grid gap-8 my-4"
        >
          <Section title="Business Details" className="grid-cols-1 ">
            <TextInputBasic
              disabled={disabled}
              register={register}
              name={`name`}
              type={'text'}
              label="Business Name"
              placeholder={disabled ? 'N/A' : 'Business Name'}
              errors={errors}
            />
            <MaskedInputBasic
              control={control}
              name={`ein`}
              label="EIN"
              disabled={disabled}
              mask="99-9999999"
              type="maskedInput"
              placeholder={disabled ? 'N/A' : 'XX-XXXXXXX'}
              errors={errors}
            />
            <SelectInputBasic
              control={control}
              name={`business_type`}
              disabled={disabled}
              label="Business Type"
              options={BusinessTypesOptions}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Business Type'}
              errors={errors}
            />
            <SelectInputBasic
              control={control}
              disabled={disabled}
              name={`specialization_id`}
              label="Industry"
              options={specializations || []}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Industry'}
              errors={errors}
            />
          </Section>
          <Section title="Formation Info" className="grid-cols-2">
            <DateInputBasic
              control={control}
              name={`formation_date`}
              type={'date'}
              placeholder={disabled ? 'N/A' : 'mm/dd/yyyy'}
              label="Formation Date"
              errors={errors}
              disabled={disabled}
            />

            <SelectInputBasic
              control={control}
              name={`formation_state`}
              label="Formation State"
              options={states}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Formation State'}
              errors={errors}
              disabled={disabled}
            />
          </Section>

          <Section title="Phone Number" className="grid-cols-2">
            <PhoneNumInputBasic
              control={control}
              name="phone_number"
              label="Phone Number"
              placeholder={disabled ? 'N/A' : 'Enter phone number'}
              errors={errors}
              disabled={disabled}
            />
          </Section>
          <Section title="Address" className="grid-cols-3">
            <div className="col-span-4">
              <TextInputBasic
                register={register}
                name={`address`}
                type={'text'}
                label="Address"
                placeholder={disabled ? 'N/A' : 'Address'}
                errors={errors}
                disabled={disabled}
              />
            </div>

            <TextInputBasic
              register={register}
              name={`city`}
              type={'text'}
              label="City"
              placeholder={disabled ? 'N/A' : 'City'}
              errors={errors}
              disabled={disabled}
            />

            <SelectInputBasic
              control={control}
              name={`state`}
              label="State"
              options={states}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select State'}
              errors={errors}
              disabled={disabled}
            />

            <TextInputBasic
              register={register}
              name="zipcode"
              label="Zipcode"
              placeholder={disabled ? 'N/A' : 'Zipcode'}
              type="text"
              errors={errors}
              disabled={disabled}
            />
          </Section>

          <Section title="Start of Fiscal Year" className="grid-cols-3">
            <SelectInputBasic
              control={control}
              name={`fiscal_year_start`}
              label="Month"
              options={FiscalYearStartOptions}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Month'}
              errors={errors}
              disabled={disabled}
            />
          </Section>

          <Section title="Ownership" className="grid-cols-3">
            <SelectInputBasic
              control={control}
              name={`ownership`}
              label="This business is owned by"
              options={OwenrshipOptions}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Ownership'}
              errors={errors}
              disabled={disabled}
            />
          </Section>

          <Section
            Header={
              <DashboardContainerHeader
                title="Business Owner(s)"
                children={
                  <>
                    {!disabled && (
                      <Button
                        type="button"
                        className="max-w-min"
                        onClick={() => append(OwnerObject)}
                      >
                        <AddIcon /> Add New Owner
                      </Button>
                    )}
                  </>
                }
              />
            }
          >
            {businessOwners.map((item, index) => (
              <Section
                key={index}
                Header={
                  <DashboardContainerHeader
                    title={
                      <p className="text-green text-base font-bold capitalize">
                        Owner {index + 1}
                      </p>
                    }
                  >
                    {!disabled && client?.id !== item.user_id && (
                      <Button
                        theme="red-outline"
                        type="button"
                        className="max-w-min"
                        onClick={() => remove(index)}
                      >
                        <TrashCanSolidIcon />
                      </Button>
                    )}
                  </DashboardContainerHeader>
                }
              >
                {/* Add New Client Button with Label */}
                {!disabled && (
                  <div className="mb-2 flex items-center justify-end gap-2">
                    {/* Label on the Left */}
                    <span className="text-xs text-gray-500 font-semibold">
                      Click here to invite a new client to portal
                    </span>

                    {/* Add New Client Button */}
                    <AddNewClientButton
                      refetchAfterAdd={() => {
                        // Dynamically update the business owners if needed
                        console.log('New client added successfully!');
                      }}
                    />
                  </div>
                )}
                <div className="w-1/2">
                  {isClient && item.user && (
                    <UserBox
                      user={item.user}
                      theme="white"
                      px="1"
                      py=""
                      size="xs"
                    />
                  )}
                  {!isClient && (
                    <ClientBusinessTaskTaxReturnSelect
                      clientId={item.user_id}
                      excludeClientIds={(businessOwners || [])
                        .filter((o) => o.user_id)
                        .map((o) => o.user_id!)}
                      lockClient={item.user_id === client?.id && client}
                      onChange={({ clientId }) => {
                        update(index, {
                          ...item,
                          user_id: clientId,
                        });
                      }}
                      withTaskSelector={false}
                      withProjectSelector={false}
                      withBusinessSelector={false}
                    />
                  )}
                </div>
                <div className="flex w-full mb-2 gap-2 md-max:grid ">
                  <div className="w-1/2  md-max:w-full">
                    <TextInputBasic
                      register={register}
                      disabled={disabled}
                      name={`business_owners.${index}.firstname`}
                      type={'text'}
                      label="First Name"
                      placeholder={disabled ? 'N/A' : 'First Name'}
                      errors={errors}
                    />
                  </div>
                  <div className="w-1/2 mr-4   md-max:w-full  md-max:mr-4">
                    <TextInputBasic
                      register={register}
                      disabled={disabled}
                      name={`business_owners.${index}.lastname`}
                      type={'text'}
                      label="Last Name"
                      placeholder={disabled ? 'N/A' : 'Last Name'}
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="flex w-full mb-3">
                  <div className="w-1/2  md-max:w-full  md-max:mr-4">
                    <TextInputBasic
                      register={register}
                      disabled={disabled}
                      name={`business_owners.${index}.ownership_percentage`}
                      type={'number'}
                      label="Ownership Percentage"
                      placeholder={disabled ? 'N/A' : 'Ownership Percentage'}
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="flex w-full mb-3">
                  <div className="w-1/2  md-max:w-full  md-max:mr-4">
                    <MaskedInputBasic
                      control={control}
                      disabled={disabled}
                      name={`business_owners.${index}.ssn`}
                      label="Social Security Number (Optional)"
                      mask="999-99-9999"
                      type="maskedInput"
                      placeholder={disabled ? 'N/A' : 'SSN'}
                      errors={errors}
                    />
                  </div>
                </div>
                <p className=" font-bold text-regular mt-7 text-gray-700">
                  Address (Optional)
                </p>
                <div className="flex w-full mb-3">
                  <div className="w-full mr-4  md-max:w-full  md-max:mr-4">
                    <TextInputBasic
                      register={register}
                      disabled={disabled}
                      name={`business_owners.${index}.address`}
                      type={'text'}
                      label="Address"
                      placeholder={disabled ? 'N/A' : 'Address'}
                      errors={errors}
                    />
                  </div>
                </div>
                <div className="flex w-full mb-2 gap-2  md-max:grid  ">
                  <div className="w-1/2  md-max:w-full">
                    <SelectInputBasic
                      control={control}
                      name={`business_owners.${index}.country`}
                      label="Country"
                      options={countries}
                      disabled={disabled}
                      type="select"
                      placeholder={disabled ? 'N/A' : 'Select Country'}
                      errors={errors}
                    />
                  </div>
                  <div className="w-1/2 mr-4   md-max:w-full  md-max:mr-4">
                    <SelectInputBasic
                      control={control}
                      name={`business_owners.${index}.state`}
                      label="State"
                      options={states}
                      disabled={disabled}
                      type="select"
                      placeholder={disabled ? 'N/A' : 'Select State'}
                      errors={errors}
                    />
                  </div>
                </div>
                <div className="flex w-full mb-2 gap-2  md-max:grid ">
                  <div className="w-1/2  md-max:w-full">
                    <TextInputBasic
                      register={register}
                      name={`business_owners.${index}.city`}
                      type={'text'}
                      label="City"
                      placeholder={disabled ? 'N/A' : 'City'}
                      errors={errors}
                      disabled={disabled}
                    />
                  </div>
                  <div className="w-1/2 mr-4   md-max:w-full  md-max:mr-4">
                    <TextInputBasic
                      register={register}
                      name={`business_owners.${index}.zipcode`}
                      type={'text'}
                      label="Zipcode"
                      disabled={disabled}
                      placeholder={disabled ? 'N/A' : 'Zipcode'}
                      errors={errors}
                    />
                  </div>
                </div>
              </Section>
            ))}
          </Section>

          <Section title="Current or Proir Year" className="grid-cols-1">
            <SelectInputBasic
              control={control}
              name={`bookkeeping_software`}
              label="What bookkeeping software did you use?"
              options={BookkeepingSoftwareOptions}
              type="select"
              placeholder={disabled ? 'N/A' : 'Select Bookkeeping Software'}
              errors={errors}
              disabled={disabled}
            />

            <Controller
              name="operational"
              control={control}
              render={({ field: { value: checked, onChange } }) => (
                <Label label="Was your business operational this year?">
                  <div className="flex items-center w-full gap-4">
                    {[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ].map((_) => (
                      <div key={_.label} className="p-2 form__input">
                        <RadioInput
                          checked={_.value === checked}
                          label={_.label}
                          name={'operational'}
                          disabled={disabled}
                          onClick={() => {
                            onChange &&
                              onChange(
                                _.value !== checked ? _.value : undefined,
                              );
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </Label>
              )}
            />
          </Section>
        </form>
      </ScrollContainer>
    </>
  );
}

function Section({
  children,
  title,
  Header: _Header,
  className = '',
  onClick,
}: {
  children: ReactNode;
  title?: ReactNode;
  onClick?: () => void;
  Header?: ReactNode;
  className?: string;
}) {
  const Header = _Header || (
    <DashboardContainerHeader
      title={<span className="text-green">{title}</span>}
    />
  );
  return (
    <div className="grid  shadow rounded-lg bg-white border overflow-hidden border-gray-100">
      {Header}
      <div
        className={`grid py-2  border-gray-100 pb-4 px-4 gap-3 w-full items-center ${className}`}
      >
        {children}
      </div>
    </div>
  );
}
